import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { listingsAPI, purchasesAPI } from '../api';
import DeliveryMap from '../components/DeliveryMap';
import ComprehensiveRatingModal from '../components/ComprehensiveRatingModal';
import { geocodeLocation } from '../utils/geolocation';
import { createSlug } from '../utils/slug';
import { addressStreetLineOnly } from '../utils/addressUtils';

interface Purchase {
  id: number;
  listing_id: number;
  buyer_id: number;
  seller_id: number;
  delivery_address: string;
  status: string;
  created_at: string;
  title?: string;
  price?: number;
  item_price?: number;
  shipping_fee?: number;
  sales_tax?: number;
  total_amount?: number;
  driver_latitude?: number | null;
  driver_longitude?: number | null;
  delivery_latitude?: number | null;
  delivery_longitude?: number | null;
  image_url?: string;
  seller_name?: string;
  is_rated?: number;
  delivery_photo?: string;
  store_id?: number;
  store_name?: string;
  is_store_rated?: number;
  is_item_rated?: number;
  city_name?: string;
  state_name?: string;
  state_abbreviation?: string;
  delivery_time_option?: string;
  shipped_at?: string;
  estimated_arrival?: string;
}

const PAGE_SIZE = 20;

const MyOrders: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Purchase[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sellerLocations, setSellerLocations] = useState<{ [key: number]: { lat: number; lng: number } }>({});
  const [ratingModal, setRatingModal] = useState<{
    isOpen: boolean;
    purchaseId: number;
    listingId: number;
    listingTitle: string;
    sellerId: number;
    sellerName: string;
    storeId?: number;
    storeName?: string;
  } | null>(null);
  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async (append = false) => {
    if (append) setLoadingMore(true);
    else { setOffset(0); setHasMore(true); }
    try {
      const fetchOffset = append ? offset : 0;
      const data = await purchasesAPI.getMyOrders({ limit: PAGE_SIZE, offset: fetchOffset });
      if (append) {
        setOrders(prev => [...prev, ...(data || [])]);
      } else {
        setOrders(data || []);
      }
      setHasMore(Array.isArray(data) && data.length >= PAGE_SIZE);
      setOffset(fetchOffset + (Array.isArray(data) ? data.length : 0));
      setLoading(false);
      
      // Geocode seller locations in the background (non-blocking)
      if (data.length > 0) {
        const locationPromises = data.map(async (order: Purchase) => {
          if (order.city_name && order.state_abbreviation) {
            try {
              const coords = await geocodeLocation(order.city_name, order.state_abbreviation);
              if (coords) {
                return { orderId: order.id, coords };
              }
            } catch (err) {
              console.error('Geocoding error for order', order.id, err);
            }
          }
          return null;
        });
        
        // Don't await - let it run in background
        Promise.all(locationPromises).then(locations => {
          const locationMap: { [key: number]: { lat: number; lng: number } } = {};
          locations.forEach((loc) => {
            if (loc) {
              locationMap[loc.orderId] = loc.coords;
            }
          });
          setSellerLocations(prev => (append ? { ...prev, ...locationMap } : locationMap));
        });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load orders');
      setLoading(false);
    } finally {
      if (append) setLoadingMore(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'processing':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'shipped':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'delivered':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case 'processing':
        return 'Preparing';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return '⏳';
      case 'processing':
        return '🔄';
      case 'shipped':
        return '📦';
      case 'delivered':
        return '✅';
      case 'cancelled':
        return '❌';
      default:
        return '📋';
    }
  };

  const getEstimatedDelivery = (createdAt: string, status: string, deliveryTimeOption?: string, shippedAt?: string, estimatedArrival?: string) => {
    const now = new Date();
    
    // If seller set a custom ETA, use that
    if (status === 'shipped' && estimatedArrival) {
      const etaDate = new Date(estimatedArrival);
      const hoursUntilETA = (etaDate.getTime() - now.getTime()) / (1000 * 60 * 60);
      
      if (hoursUntilETA <= 0) {
        return 'Arriving soon';
      } else if (hoursUntilETA < 0.25) {
        return 'Arriving very soon';
      } else if (hoursUntilETA < 1) {
        const minutes = Math.ceil(hoursUntilETA * 60);
        return `Arriving in ~${minutes} minutes`;
      } else if (hoursUntilETA <= 2) {
        const roundedHours = Math.ceil(hoursUntilETA * 2) / 2;
        return `Arriving in ~${roundedHours} hours`;
      } else {
        return `Arriving in ~${Math.round(hoursUntilETA)} hours`;
      }
    }
    
    // Otherwise, calculate from shipped_at or order creation
    let hoursSinceShipping = 0;
    
    // If we have a shipped_at timestamp, use that for more accurate calculation
    if (status === 'shipped' && shippedAt) {
      const shippedDate = new Date(shippedAt);
      hoursSinceShipping = (now.getTime() - shippedDate.getTime()) / (1000 * 60 * 60);
    } else {
      // Fallback to order creation time
      const orderDate = new Date(createdAt);
      const hoursSinceOrder = (now.getTime() - orderDate.getTime()) / (1000 * 60 * 60);
      // Assume processing took 1 hour before shipping
      hoursSinceShipping = Math.max(0, hoursSinceOrder - 1);
    }
    
    // Get max delivery hours based on option
    const getMaxDeliveryHours = (option?: string): number => {
      switch (option) {
        case '1-2':
          return 2;
        case '3-6':
          return 6;
        case '24':
          return 24;
        default:
          return 2; // Default to 2 hours
      }
    };
    
    const maxDeliveryHours = getMaxDeliveryHours(deliveryTimeOption);
    const deliveryTimeDisplay = deliveryTimeOption === '1-2' ? '1-2 hours' :
                                deliveryTimeOption === '3-6' ? '3-6 hours' :
                                deliveryTimeOption === '24' ? '24 hours' : '1-2 hours';
    
    if (status === 'delivered') {
      return 'Delivered';
    }
    
    if (status === 'shipped') {
      // Calculate remaining delivery time from when it was actually shipped
      const estimatedHours = maxDeliveryHours - hoursSinceShipping;
      
      if (estimatedHours > 0) {
        if (estimatedHours < 0.25) {
          // Less than 15 minutes
          return 'Arriving very soon';
        } else if (estimatedHours < 1) {
          const minutes = Math.ceil(estimatedHours * 60);
          return `Arriving in ~${minutes} minutes`;
        } else if (estimatedHours <= 2) {
          // For 1-2 hour deliveries, show more precise time
          const roundedHours = Math.ceil(estimatedHours * 2) / 2;
          if (roundedHours === 1) {
            return 'Arriving in ~1 hour';
          } else {
            return `Arriving in ~${roundedHours} hours`;
          }
        } else {
          // For longer deliveries, round to whole hours
          return `Arriving in ~${Math.round(estimatedHours)} hours`;
        }
      }
      return 'Arriving soon';
    }
    
    if (status === 'processing') {
      return `Preparing for shipment (${deliveryTimeDisplay})`;
    }
    
    if (status === 'pending') {
      return `Processing order (${deliveryTimeDisplay} until shipment)`;
    }
    
    return `Estimated delivery: ${deliveryTimeDisplay}`;
  };

  const formatPrice = (price: number | null | undefined) => {
    if (!price) return 'Free';
    return `$${price.toLocaleString()}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
          <p className="mt-4 text-gray-600">Loading your orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">My Orders</h1>
          <p className="text-gray-600">Track your purchases and delivery status</p>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {orders.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <div className="text-6xl mb-4">📦</div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">No orders yet</h2>
            <p className="text-gray-600 mb-6">Start shopping to see your orders here</p>
            <button
              onClick={() => navigate('/')}
              className="bg-green-500 hover:bg-green-600 text-white font-medium py-2 px-6 rounded-lg transition"
            >
              Browse Listings
            </button>
          </div>
        ) : (
          <>
          <div className="space-y-4">
            {orders.map((order) => (
              <div key={order.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start space-x-4 flex-1">
                      {order.image_url ? (
                        <img
                          src={order.image_url}
                          alt={order.title}
                          className="w-20 h-20 object-cover rounded-lg"
                        />
                      ) : (
                        <div className="w-20 h-20 bg-gray-200 rounded-lg flex items-center justify-center">
                          <span className="text-3xl">📦</span>
                        </div>
                      )}
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-gray-800 mb-1">
                          {order.title || 'Item'}
                        </h3>
                        {order.store_id && order.store_name ? (
                          <Link
                            to={`/stores/${createSlug(order.store_name)}`}
                            className="text-gray-600 text-sm mb-2 hover:text-green-600 transition block"
                          >
                            Store: {order.store_name}
                          </Link>
                        ) : (
                          <Link
                            to={`/sellers/${order.seller_id}`}
                            className="text-gray-600 text-sm mb-2 hover:text-green-600 transition block"
                          >
                            Seller: {order.seller_name || 'Unknown'}
                          </Link>
                        )}
                        <p className="text-lg font-bold text-blue-600">
                          {order.total_amount != null
                            ? `$${Number(order.total_amount).toFixed(2)}`
                            : (order.item_price != null && (order.shipping_fee != null || order.sales_tax != null))
                              ? `$${(Number(order.item_price) + Number(order.shipping_fee ?? 0) + Number(order.sales_tax ?? 0)).toFixed(2)}`
                              : formatPrice(order.price)}
                        </p>
                        {(order.item_price != null || order.shipping_fee != null || order.sales_tax != null) && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            {[
                              order.item_price != null && `Item $${Number(order.item_price).toFixed(2)}`,
                              order.shipping_fee != null && Number(order.shipping_fee) > 0 && `Delivery $${Number(order.shipping_fee).toFixed(2)}`,
                              order.sales_tax != null && Number(order.sales_tax) > 0 && `Tax $${Number(order.sales_tax).toFixed(2)}`,
                            ].filter(Boolean).join(' + ')}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className={`px-4 py-2 rounded-lg border ${getStatusColor(order.status)}`}>
                      <div className="flex items-center space-x-2">
                        <span className="text-xl">{getStatusIcon(order.status)}</span>
                        <span className="font-semibold">{getStatusLabel(order.status)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4 mt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Order Total</p>
                        <p className="font-medium font-semibold text-gray-900">
                          {order.total_amount != null
                            ? `$${Number(order.total_amount).toFixed(2)}`
                            : (order.item_price != null && (order.shipping_fee != null || order.sales_tax != null))
                              ? `$${(Number(order.item_price) + Number(order.shipping_fee ?? 0) + Number(order.sales_tax ?? 0)).toFixed(2)}`
                              : order.price != null
                                ? `$${Number(order.price).toFixed(2)}`
                                : '—'}
                        </p>
                        {(order.item_price != null || order.shipping_fee != null || order.sales_tax != null) && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            Item ${Number(order.item_price ?? order.price ?? 0).toFixed(2)}
                            {Number(order.shipping_fee ?? 0) > 0 && ` + Delivery $${Number(order.shipping_fee).toFixed(2)}`}
                            {Number(order.sales_tax ?? 0) > 0 && ` + Tax $${Number(order.sales_tax).toFixed(2)}`}
                          </p>
                        )}
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Order Date</p>
                        <p className="font-medium">{formatDate(order.created_at)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Delivery Address</p>
                        <p className="font-medium">
                          {addressStreetLineOnly(null, order.delivery_address)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Delivery Status</p>
                        <p className="font-medium text-green-600">
                          🚚 {getEstimatedDelivery(order.created_at, order.status, order.delivery_time_option, order.shipped_at, order.estimated_arrival)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Order ID</p>
                        <p className="font-medium text-gray-600">#{order.id}</p>
                      </div>
                    </div>
                    
                    {/* Track Order Link for Active Orders */}
                    {['pending', 'processing', 'shipped'].includes(order.status?.toLowerCase()) && (
                      <div className="mt-4 pt-4 border-t">
                        <Link
                          to={`/track-order/${order.id}`}
                          className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition"
                        >
                          Track Order
                        </Link>
                      </div>
                    )}
                  </div>

                  {order.status === 'pending' && (
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800">
                        ⏳ Your order is being processed. The seller will prepare your item for delivery.
                      </p>
                    </div>
                  )}

                  {order.status === 'processing' && (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800">
                        🔄 Your order is being prepared for shipment. {getEstimatedDelivery(order.created_at, order.status, order.delivery_time_option, order.shipped_at, order.estimated_arrival)}
                      </p>
                    </div>
                  )}

                  {order.status === 'shipped' && (
                    <div className="mt-4 space-y-3">
                      <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                        <p className="text-sm text-purple-800">
                          📦 Your order is on the way! {getEstimatedDelivery(order.created_at, order.status, order.delivery_time_option, order.shipped_at, order.estimated_arrival)}
                        </p>
                      </div>
                      <DeliveryMap
                        deliveryAddress={order.delivery_address}
                        sellerLocation={sellerLocations[order.id]}
                        status={order.status}
                        orderDate={order.created_at}
                        deliveryTimeOption={order.delivery_time_option}
                        driverLocation={
                          order.driver_latitude != null && order.driver_longitude != null
                            ? { lat: order.driver_latitude, lng: order.driver_longitude }
                            : undefined
                        }
                        deliveryCoords={
                          order.delivery_latitude != null && order.delivery_longitude != null
                            ? { lat: order.delivery_latitude, lng: order.delivery_longitude }
                            : undefined
                        }
                      />
                    </div>
                  )}

                  {order.status === 'processing' && (
                    <div className="mt-4 space-y-3">
                      <DeliveryMap
                        deliveryAddress={order.delivery_address}
                        sellerLocation={sellerLocations[order.id]}
                        status={order.status}
                        orderDate={order.created_at}
                        deliveryTimeOption={order.delivery_time_option}
                        driverLocation={
                          order.driver_latitude != null && order.driver_longitude != null
                            ? { lat: order.driver_latitude, lng: order.driver_longitude }
                            : undefined
                        }
                        deliveryCoords={
                          order.delivery_latitude != null && order.delivery_longitude != null
                            ? { lat: order.delivery_latitude, lng: order.delivery_longitude }
                            : undefined
                        }
                      />
                    </div>
                  )}

                  {order.status === 'delivered' && (
                    <div className="mt-4 rounded-xl overflow-hidden border border-gray-100 bg-white shadow-sm">
                      {/* Delivery photo - DoorDash style, prominent at top */}
                      {order.delivery_photo && (
                        <div className="relative aspect-video w-full bg-gray-100">
                          <img
                            src={order.delivery_photo}
                            alt="Delivery confirmation"
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                          <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2 text-white">
                            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-green-500">
                              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                            </span>
                            <span className="font-semibold text-sm drop-shadow">Delivered</span>
                          </div>
                        </div>
                      )}
                      {/* Success banner */}
                      <div className="px-4 py-3 bg-green-50 border-b border-green-100">
                        <p className="text-sm font-medium text-green-800">
                          ✅ Your order has been delivered! Thank you!
                        </p>
                      </div>
                      {/* Item, buyer, address, amount - DoorDash style card */}
                      <div className="p-4 space-y-4">
                        {/* Item row */}
                        <div className="flex gap-3">
                          {order.image_url ? (
                            <img src={order.image_url} alt={order.title} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-2xl">📦</span>
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-900 truncate">{order.title || 'Item'}</p>
                            {order.store_name ? (
                              <p className="text-sm text-gray-500">Store: {order.store_name}</p>
                            ) : (
                              <p className="text-sm text-gray-500">Seller: {order.seller_name || 'Unknown'}</p>
                            )}
                            <p className="text-base font-bold text-gray-900 mt-1">
                              {order.price != null ? `$${Number(order.price).toFixed(2)}` : '—'}
                            </p>
                          </div>
                        </div>
                        {/* Delivered to + Address */}
                        <div className="space-y-2 pt-3 border-t border-gray-100">
                          <div>
                            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Delivered to</p>
                            <p className="text-sm font-medium text-gray-900">
                              {addressStreetLineOnly(null, order.delivery_address)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Order total</p>
                            <p className="text-lg font-bold text-gray-900">
                              {order.total_amount != null
                                ? `$${Number(order.total_amount).toFixed(2)}`
                                : order.item_price != null
                                  ? `$${(Number(order.item_price) + Number(order.shipping_fee ?? 0) + Number(order.sales_tax ?? 0)).toFixed(2)}`
                                  : order.price != null
                                    ? `$${Number(order.price).toFixed(2)}`
                                    : '—'}
                            </p>
                          </div>
                        </div>
                        {/* Rate button */}
                        <div className="pt-2">
                          {order.is_item_rated && (order.is_rated || order.is_store_rated) ? (
                            <p className="text-sm text-gray-500 flex items-center gap-2">
                              <span className="text-green-500">✓</span> You have already rated this purchase
                            </p>
                          ) : (
                            <button
                              onClick={() =>
                                setRatingModal({
                                  isOpen: true,
                                  purchaseId: order.id,
                                  listingId: order.listing_id,
                                  listingTitle: order.title || 'Item',
                                  sellerId: order.seller_id,
                                  sellerName: order.seller_name || 'Seller',
                                  storeId: order.store_id,
                                  storeName: order.store_name,
                                })
                              }
                              className="w-full py-3 px-4 bg-gray-900 hover:bg-gray-800 text-white font-medium rounded-lg transition text-sm"
                            >
                              ⭐ Rate your order
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          {hasMore && (
            <div className="mt-6 flex justify-center">
              <button
                onClick={() => loadOrders(true)}
                disabled={loadingMore}
                className="px-6 py-3 bg-green-500 hover:bg-green-600 disabled:opacity-70 text-white font-medium rounded-lg transition flex items-center gap-2"
              >
                {loadingMore ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    Loading...
                  </>
                ) : (
                  'Load more'
                )}
              </button>
            </div>
          )}
          </>
        )}

        {ratingModal && (
          <ComprehensiveRatingModal
            isOpen={ratingModal.isOpen}
            onClose={() => setRatingModal(null)}
            purchaseId={ratingModal.purchaseId}
            listingId={ratingModal.listingId}
            listingTitle={ratingModal.listingTitle}
            sellerId={ratingModal.sellerId}
            sellerName={ratingModal.sellerName}
            storeId={ratingModal.storeId}
            storeName={ratingModal.storeName}
            onRated={() => {
              loadOrders();
            }}
          />
        )}
      </div>
    </div>
  );
};

export default MyOrders;

